<script setup>
import { computed, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS } from '../constants';
import { useToggle } from '@vueuse/core';

import MetricCard from './overview/MetricCard.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const currentUserChatAvailability = useMapGetter('getCurrentUserChatAvailability');
const currentUserCallAvailability = useMapGetter('getCurrentUserCallAvailability');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const searchQuery = ref('');
const statusFilter = ref('all');
const isRefreshing = ref(false);

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const conversationMetrics = computed(() => {
  let metric = {};
  Object.keys(accountConversationMetric.value).forEach(key => {
    const metricName = t(`${accounti18nKey}.${OVERVIEW_METRICS[key]}`);
    metric[metricName] = accountConversationMetric.value[key];
  });
  return metric;
});

const getAgentBaseAvailability = agent => {
  if (currentUser.value?.id && agent.id === currentUser.value.id) {
    return currentUserAvailability.value || agent.availability_status || 'offline';
  }
  return agent.availability_status || 'offline';
};

const isAgentOnline = agent => {
  return getAgentBaseAvailability(agent) === 'online';
};

const getAgentChatAvailability = agent => {
  if (currentUser.value?.id && agent.id === currentUser.value.id) {
    return currentUserChatAvailability.value || agent.chat_availability || 'online';
  }
  return agent.chat_availability || 'online';
};

const getAgentCallAvailability = agent => {
  if (currentUser.value?.id && agent.id === currentUser.value.id) {
    return currentUserCallAvailability.value || agent.call_availability || 'online';
  }
  return agent.call_availability || 'online';
};

// Agent is Chat Online ONLY if actively logged in/online AND chat availability is online
const isAgentChatOnline = agent => {
  return isAgentOnline(agent) && getAgentChatAvailability(agent) === 'online';
};

// Agent is Call Online ONLY if actively logged in/online AND call availability is online
const isAgentCallOnline = agent => {
  return isAgentOnline(agent) && getAgentCallAvailability(agent) === 'online';
};

// Agent is active if they are Chat Online OR Call Online
const isAgentAnyOnline = agent => {
  return isAgentChatOnline(agent) || isAgentCallOnline(agent);
};

const channelStatusCounts = computed(() => {
  let chatOnline = 0;
  let callOnline = 0;
  let offline = 0;

  (agents.value || []).forEach(agent => {
    const chat = isAgentChatOnline(agent);
    const call = isAgentCallOnline(agent);

    if (chat) chatOnline += 1;
    if (call) callOnline += 1;
    if (!chat && !call) offline += 1;
  });

  return {
    chatOnline,
    callOnline,
    offline,
  };
});

const agentHeader = computed(() => {
  const onlineCount = (agents.value || []).filter(isAgentAnyOnline).length;
  return `Agents (${onlineCount} online)`;
});

const filteredAgents = computed(() => {
  let list = [...(agents.value || [])];

  if (statusFilter.value === 'chat_online') {
    list = list.filter(isAgentChatOnline);
  } else if (statusFilter.value === 'call_online') {
    list = list.filter(isAgentCallOnline);
  } else if (statusFilter.value === 'offline') {
    list = list.filter(agent => !isAgentAnyOnline(agent));
  }

  const query = searchQuery.value.trim().toLowerCase();
  if (query) {
    list = list.filter(agent => {
      const name = (agent.available_name || agent.name || '').toLowerCase();
      const email = (agent.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }

  // Priority: online for both (1) > online for one (2) > offline (3)
  return list.sort((a, b) => {
    const pA = isAgentChatOnline(a) && isAgentCallOnline(a) ? 1 : (isAgentAnyOnline(a) ? 2 : 3);
    const pB = isAgentChatOnline(b) && isAgentCallOnline(b) ? 1 : (isAgentAnyOnline(b) ? 2 : 3);
    if (pA !== pB) return pA - pB;
    const nameA = a.available_name || a.name || '';
    const nameB = b.available_name || b.name || '';
    return nameA.localeCompare(nameB);
  });
});

const getStatusDotClass = agent => {
  if (statusFilter.value === 'chat_online') {
    return isAgentChatOnline(agent) ? 'bg-n-teal-9' : 'bg-n-slate-8';
  }
  if (statusFilter.value === 'call_online') {
    return isAgentCallOnline(agent) ? 'bg-n-teal-9' : 'bg-n-slate-8';
  }
  return isAgentAnyOnline(agent) ? 'bg-n-teal-9' : 'bg-n-slate-8';
};

const refreshAgents = async () => {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    await store.dispatch('agents/get');
  } finally {
    setTimeout(() => {
      isRefreshing.value = false;
    }, 400);
  }
};

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <!-- Top: Live Agent Status Card (Full Width) -->
    <div class="w-full">
      <MetricCard :header="agentHeader">
        <template #control>
          <div class="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <!-- Filter Pills -->
            <div
              class="flex items-center gap-1 bg-n-alpha-1 dark:bg-n-solid-1 p-0.5 rounded-lg text-xs"
            >
              <button
                type="button"
                class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                :class="
                  statusFilter === 'all'
                    ? 'bg-n-slate-12 text-n-background dark:bg-n-slate-12 dark:text-n-background shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="statusFilter = 'all'"
              >
                All ({{ agents.length }})
              </button>
              <button
                type="button"
                class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                :class="
                  statusFilter === 'chat_online'
                    ? 'bg-n-teal-9 text-white shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="statusFilter = 'chat_online'"
              >
                <Icon icon="i-lucide-message-square" class="size-3" />
                Chat Online ({{ channelStatusCounts.chatOnline }})
              </button>
              <button
                type="button"
                class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                :class="
                  statusFilter === 'call_online'
                    ? 'bg-n-teal-9 text-white shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="statusFilter = 'call_online'"
              >
                <Icon icon="i-lucide-phone" class="size-3" />
                Call Online ({{ channelStatusCounts.callOnline }})
              </button>
              <button
                type="button"
                class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                :class="
                  statusFilter === 'offline'
                    ? 'bg-n-slate-9 text-white shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="statusFilter = 'offline'"
              >
                <span
                  class="size-1.5 rounded-full inline-block"
                  :class="statusFilter === 'offline' ? 'bg-white' : 'bg-n-slate-8'"
                />
                Offline ({{ channelStatusCounts.offline }})
              </button>
            </div>

            <!-- Search input -->
            <div class="relative flex items-center">
              <Icon
                icon="i-lucide-search"
                class="absolute left-2.5 size-3.5 text-n-slate-10 pointer-events-none"
              />
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Search ..."
                class="h-8 w-36 sm:w-48 pl-8 pr-3 text-xs rounded-lg border-0 bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 outline outline-1 outline-n-weak focus:outline-n-brand transition-all"
              />
            </div>

            <!-- Refresh button -->
            <button
              type="button"
              title="Refresh"
              class="p-2 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
              :class="{ 'animate-spin': isRefreshing }"
              @click="refreshAgents"
            >
              <Icon icon="i-lucide-rotate-cw" class="size-4 block" />
            </button>
          </div>
        </template>

        <div class="flex flex-col w-full min-w-0">
          <!-- Agent list -->
          <div
            class="flex flex-col divide-y divide-n-weak/30 max-h-[340px] overflow-y-auto pr-1"
          >
            <div
              v-for="agent in filteredAgents"
              :key="agent.id"
              class="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-solid-3 transition-colors"
            >
              <div class="flex items-center gap-3 min-w-0">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.available_name || agent.name || 'Agent'"
                  :size="28"
                  rounded-full
                />
                <div class="flex items-center gap-2 min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ agent.available_name || agent.name }}
                  </span>
                  <span
                    class="size-2 rounded-full inline-block shrink-0"
                    :class="getStatusDotClass(agent)"
                  />
                </div>
              </div>

              <!-- Channel Status Indicators -->
              <div v-if="statusFilter === 'chat_online'" class="shrink-0 ml-2">
                <span
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                  :class="
                    isAgentChatOnline(agent)
                      ? 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-3/20 dark:text-n-teal-10 font-medium'
                      : 'bg-n-alpha-1 text-n-slate-10 dark:text-n-slate-11'
                  "
                >
                  <Icon icon="i-lucide-message-square" class="size-3" />
                  {{ isAgentChatOnline(agent) ? 'Chat Online' : 'Chat Offline' }}
                </span>
              </div>

              <div v-else-if="statusFilter === 'call_online'" class="shrink-0 ml-2">
                <span
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                  :class="
                    isAgentCallOnline(agent)
                      ? 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-3/20 dark:text-n-teal-10 font-medium'
                      : 'bg-n-alpha-1 text-n-slate-10 dark:text-n-slate-11'
                  "
                >
                  <Icon icon="i-lucide-phone" class="size-3" />
                  {{ isAgentCallOnline(agent) ? 'Call Online' : 'Call Offline' }}
                </span>
              </div>

              <div v-else-if="isAgentAnyOnline(agent)" class="flex items-center gap-1.5 shrink-0 ml-2">
                <span
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium"
                  :class="
                    isAgentChatOnline(agent)
                      ? 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-3/20 dark:text-n-teal-10'
                      : 'bg-n-alpha-1 text-n-slate-10 dark:text-n-slate-11'
                  "
                >
                  <Icon icon="i-lucide-message-square" class="size-3" />
                  {{ isAgentChatOnline(agent) ? 'Chat Online' : 'Chat Offline' }}
                </span>

                <span
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium"
                  :class="
                    isAgentCallOnline(agent)
                      ? 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-3/20 dark:text-n-teal-10'
                      : 'bg-n-alpha-1 text-n-slate-10 dark:text-n-slate-11'
                  "
                >
                  <Icon icon="i-lucide-phone" class="size-3" />
                  {{ isAgentCallOnline(agent) ? 'Call Online' : 'Call Offline' }}
                </span>
              </div>

              <div v-else class="shrink-0 ml-2">
                <span class="text-xs capitalize text-n-slate-11">
                  Offline
                </span>
              </div>
            </div>

            <!-- Empty state -->
            <div
              v-if="filteredAgents.length === 0"
              class="py-10 text-center text-sm text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.NO_RESULTS') }}
            </div>
          </div>
        </div>
      </MetricCard>
    </div>

    <!-- Bottom: Open Conversations Card (Full Width) -->
    <div class="w-full conversation-metric">
      <MetricCard
        :header="t(`${accounti18nKey}.HEADER`)"
        :is-loading="uiFlags.isFetchingAccountConversationMetric"
        :loading-message="t(`${accounti18nKey}.LOADING_MESSAGE`)"
      >
        <template v-if="teams.length" #control>
          <div
            v-on-clickaway="() => toggleDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedTeamLabel"
              class="capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showDropdown"
              :menu-items="teamMenuList"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
              label-class="capitalize"
              @action="handleAction($event)"
            />
          </div>
        </template>
        <div
          v-for="(metric, name, index) in conversationMetrics"
          :key="index"
          class="flex-1 min-w-0 pb-2"
        >
          <h3 class="text-base text-n-slate-11">
            {{ name }}
          </h3>
          <p class="text-n-slate-12 text-3xl mb-0 mt-1">
            {{ metric }}
          </p>
        </div>
      </MetricCard>
    </div>
  </div>
</template>
